import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Target, ArrowLeft, Plus, TrendingUp, Calendar, Trash2, 
  CheckCircle2, XCircle, ChevronRight, BarChart3, Users, Clock, Copy,
  Download, LineChart as LineChartIcon, ListChecks, Bell, CalendarPlus, Mail, Edit2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASELINE_TYPE_OPTIONS = [
  { value: 'percentage', label: 'Percentage Accuracy', description: 'Track progress by % correct (0-100%)' },
  { value: 'number', label: 'Number Correct', description: 'Track progress by X correct out of Y total' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: '#5D8D78' },
  { value: 'achieved', label: 'Goal Achieved', color: '#22C55E' },
  { value: 'discontinued', label: 'Discontinued', color: '#8A9192' },
];

const IEPGoalTracker = ({ user }) => {
  const navigate = useNavigate();
  const { trackerId } = useParams();
  
  const [trackers, setTrackers] = useState([]);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [savedGoals, setSavedGoals] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddEntryDialog, setShowAddEntryDialog] = useState(false);
  const [showEditEntryDialog, setShowEditEntryDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeleteEntryDialog, setShowDeleteEntryDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showBulkEntryDialog, setShowBulkEntryDialog] = useState(false);
  const [showGraphDialog, setShowGraphDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [trackerToDelete, setTrackerToDelete] = useState(null);
  const [trackerToCopy, setTrackerToCopy] = useState(null);
  const [entryToEdit, setEntryToEdit] = useState(null);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [selectedStudentsForCopy, setSelectedStudentsForCopy] = useState([]);
  const [selectedTrackersForBulk, setSelectedTrackersForBulk] = useState([]);
  const [bulkGoalFilter, setBulkGoalFilter] = useState('');
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderMessage, setReminderMessage] = useState('');
  const chartRef = useRef(null);
  
  // Form states
  const [newTracker, setNewTracker] = useState({
    goal_bank_id: '',
    student_id: '',
    target_date: '',
    baseline_type: 'percentage',
    baseline_value: 0,
    baseline_total: 10,
    iep_due_date: ''
  });
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    score: 0,
    total_possible: 10,
    notes: '',
    data_point: ''
  });
  const [editEntry, setEditEntry] = useState({
    date: '',
    score: 0,
    total_possible: 10,
    notes: '',
    data_point: ''
  });
  const [bulkEntry, setBulkEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    score: 0,
    total_possible: 10,
    notes: '',
    data_point: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (trackerId) {
      fetchTrackerDetails(trackerId);
    }
  }, [trackerId]);

  const fetchData = async () => {
    try {
      const [trackersRes, goalsRes, foldersRes] = await Promise.all([
        axios.get(`${API}/iep-goal-tracker`, { withCredentials: true }),
        axios.get(`${API}/goal-bank`, { withCredentials: true }),
        axios.get(`${API}/folders`, { withCredentials: true })
      ]);
      setTrackers(trackersRes.data);
      setSavedGoals(goalsRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackerDetails = async (id) => {
    try {
      const response = await axios.get(`${API}/iep-goal-tracker/${id}`, { withCredentials: true });
      setSelectedTracker(response.data);
    } catch (error) {
      console.error('Error fetching tracker details:', error);
      toast.error('Failed to load tracker details');
    }
  };

  const handleCreateTracker = async () => {
    if (!newTracker.goal_bank_id || !newTracker.student_id) {
      toast.error('Please select a goal and student');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/iep-goal-tracker`, {
        goal_bank_id: newTracker.goal_bank_id,
        student_id: newTracker.student_id,
        target_date: newTracker.target_date || null,
        baseline_type: newTracker.baseline_type,
        baseline_value: newTracker.baseline_value,
        baseline_total: newTracker.baseline_type === 'number' ? newTracker.baseline_total : null,
        iep_due_date: newTracker.iep_due_date || null
      }, { withCredentials: true });
      toast.success('IEP Goal tracker created!');
      setShowCreateDialog(false);
      setNewTracker({ goal_bank_id: '', student_id: '', target_date: '', baseline_type: 'percentage', baseline_value: 0, baseline_total: 10, iep_due_date: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating tracker:', error);
      toast.error('Failed to create tracker');
    } finally {
      setSaving(false);
    }
  };

  const handleAddEntry = async () => {
    if (!selectedTracker) return;
    
    const baselineType = selectedTracker.baseline_type || 'percentage';
    
    // Validation
    if (baselineType === 'number' && (!newEntry.total_possible || newEntry.total_possible < 1)) {
      toast.error('Please enter the total number of problems/steps');
      return;
    }
    
    if (baselineType === 'number' && newEntry.score > newEntry.total_possible) {
      toast.error('Score cannot exceed total possible');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/iep-goal-tracker/${selectedTracker.tracker_id}/entry`, {
        tracker_id: selectedTracker.tracker_id,
        date: newEntry.date,
        score: newEntry.score,
        total_possible: baselineType === 'number' ? newEntry.total_possible : null,
        notes: newEntry.notes,
        data_point: newEntry.data_point
      }, { withCredentials: true });
      toast.success('Progress recorded!');
      setShowAddEntryDialog(false);
      setNewEntry({ date: new Date().toISOString().split('T')[0], score: 0, total_possible: 10, notes: '', data_point: '' });
      fetchTrackerDetails(selectedTracker.tracker_id);
      fetchData(); // Refresh list
    } catch (error) {
      console.error('Error adding entry:', error);
      toast.error('Failed to record progress');
    } finally {
      setSaving(false);
    }
  };

  // Edit existing progress entry
  const handleEditEntry = async () => {
    if (!selectedTracker || !entryToEdit) return;
    
    const baselineType = selectedTracker.baseline_type || 'percentage';
    
    setSaving(true);
    try {
      await axios.put(`${API}/iep-goal-tracker/${selectedTracker.tracker_id}/entry/${entryToEdit.entry_id}`, {
        date: editEntry.date,
        score: editEntry.score,
        total_possible: baselineType === 'number' ? editEntry.total_possible : null,
        notes: editEntry.notes,
        data_point: editEntry.data_point
      }, { withCredentials: true });
      toast.success('Entry updated!');
      setShowEditEntryDialog(false);
      setEntryToEdit(null);
      setEditEntry({ date: '', score: 0, total_possible: 10, notes: '', data_point: '' });
      fetchTrackerDetails(selectedTracker.tracker_id);
      fetchData();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  // Delete progress entry
  const handleDeleteEntry = async () => {
    if (!selectedTracker || !entryToDelete) return;
    
    try {
      await axios.delete(`${API}/iep-goal-tracker/${selectedTracker.tracker_id}/entry/${entryToDelete.entry_id}`, { withCredentials: true });
      toast.success('Entry deleted');
      setShowDeleteEntryDialog(false);
      setEntryToDelete(null);
      fetchTrackerDetails(selectedTracker.tracker_id);
      fetchData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  // Open edit dialog with entry data
  const openEditEntryDialog = (entry) => {
    setEntryToEdit(entry);
    setEditEntry({
      date: entry.date,
      score: entry.score,
      total_possible: entry.total_possible || 10,
      notes: entry.notes || '',
      data_point: entry.data_point || ''
    });
    setShowEditEntryDialog(true);
  };

  const handleStatusChange = async (trackerId, newStatus) => {
    try {
      await axios.put(`${API}/iep-goal-tracker/${trackerId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Status updated to ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      fetchData();
      if (selectedTracker?.tracker_id === trackerId) {
        fetchTrackerDetails(trackerId);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTracker = async () => {
    if (!trackerToDelete) return;
    
    try {
      await axios.delete(`${API}/iep-goal-tracker/${trackerToDelete.tracker_id}`, { withCredentials: true });
      toast.success('Tracker deleted');
      setShowDeleteDialog(false);
      setTrackerToDelete(null);
      if (selectedTracker?.tracker_id === trackerToDelete.tracker_id) {
        setSelectedTracker(null);
        navigate('/iep-goal-tracker');
      }
      fetchData();
    } catch (error) {
      console.error('Error deleting tracker:', error);
      toast.error('Failed to delete tracker');
    }
  };

  const handleCopyTracker = async () => {
    if (!trackerToCopy || selectedStudentsForCopy.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    setSaving(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const studentId of selectedStudentsForCopy) {
        try {
          await axios.post(`${API}/iep-goal-tracker`, {
            goal_bank_id: trackerToCopy.goal_bank_id,
            student_id: studentId,
            target_date: trackerToCopy.target_date || null,
            baseline_type: trackerToCopy.baseline_type || 'percentage',
            baseline_value: trackerToCopy.baseline_value || 0,
            baseline_total: trackerToCopy.baseline_total || null
          }, { withCredentials: true });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Error copying tracker to student ${studentId}:`, err);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Tracker copied to ${successCount} student${successCount > 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to copy to ${errorCount} student${errorCount > 1 ? 's' : ''}`);
      }
      
      setShowCopyDialog(false);
      setTrackerToCopy(null);
      setSelectedStudentsForCopy([]);
      fetchData();
    } catch (error) {
      console.error('Error copying tracker:', error);
      toast.error('Failed to copy tracker');
    } finally {
      setSaving(false);
    }
  };

  const toggleStudentForCopy = (studentId) => {
    setSelectedStudentsForCopy(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Get students that don't already have this tracker
  const getAvailableStudentsForCopy = () => {
    if (!trackerToCopy) return folders;
    
    // Find existing trackers with same goal
    const existingStudentIds = trackers
      .filter(t => t.goal_bank_id === trackerToCopy.goal_bank_id)
      .map(t => t.student_id);
    
    return folders.filter(f => !existingStudentIds.includes(f.folder_id));
  };

  // Bulk Entry Functions
  const toggleTrackerForBulk = (trackerId) => {
    setSelectedTrackersForBulk(prev => 
      prev.includes(trackerId) 
        ? prev.filter(id => id !== trackerId)
        : [...prev, trackerId]
    );
  };

  const getTrackersForBulkEntry = () => {
    if (!bulkGoalFilter) return trackers;
    return trackers.filter(t => t.goal_bank_id === bulkGoalFilter);
  };

  const getUniqueGoalsFromTrackers = () => {
    const goalMap = new Map();
    trackers.forEach(t => {
      if (!goalMap.has(t.goal_bank_id)) {
        goalMap.set(t.goal_bank_id, { id: t.goal_bank_id, title: t.goal_title });
      }
    });
    return Array.from(goalMap.values());
  };

  const handleBulkEntry = async () => {
    if (selectedTrackersForBulk.length === 0) {
      toast.error('Please select at least one tracker');
      return;
    }
    
    setSaving(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const trackerId of selectedTrackersForBulk) {
        const tracker = trackers.find(t => t.tracker_id === trackerId);
        if (!tracker) continue;
        
        const baselineType = tracker.baseline_type || 'percentage';
        
        try {
          await axios.post(`${API}/iep-goal-tracker/${trackerId}/entry`, {
            tracker_id: trackerId,
            date: bulkEntry.date,
            score: bulkEntry.score,
            total_possible: baselineType === 'number' ? bulkEntry.total_possible : null,
            notes: bulkEntry.notes,
            data_point: bulkEntry.data_point
          }, { withCredentials: true });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Error adding entry to tracker ${trackerId}:`, err);
        }
      }
      
      if (successCount > 0) {
        toast.success(`Progress recorded for ${successCount} tracker${successCount > 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to record for ${errorCount} tracker${errorCount > 1 ? 's' : ''}`);
      }
      
      setShowBulkEntryDialog(false);
      setSelectedTrackersForBulk([]);
      setBulkEntry({ date: new Date().toISOString().split('T')[0], score: 0, total_possible: 10, notes: '', data_point: '' });
      fetchData();
      if (selectedTracker) {
        fetchTrackerDetails(selectedTracker.tracker_id);
      }
    } catch (error) {
      console.error('Error bulk entry:', error);
      toast.error('Failed to record progress');
    } finally {
      setSaving(false);
    }
  };

  // Send email reminder for IEP due date
  const handleSendReminder = async () => {
    if (!selectedTracker || !selectedTracker.iep_due_date) {
      toast.error('No IEP due date set for this tracker');
      return;
    }
    
    if (!reminderEmail) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/iep-goal-tracker/${selectedTracker.tracker_id}/send-reminder`, {
        recipient_email: reminderEmail,
        custom_message: reminderMessage || null
      }, { withCredentials: true });
      toast.success('Reminder email sent successfully!');
      setShowReminderDialog(false);
      setReminderEmail('');
      setReminderMessage('');
    } catch (error) {
      console.error('Error sending reminder:', error);
      toast.error('Failed to send reminder email');
    } finally {
      setSaving(false);
    }
  };

  // Download calendar event as ICS file
  const handleDownloadCalendarEvent = async () => {
    if (!selectedTracker || !selectedTracker.iep_due_date) {
      toast.error('No IEP due date set for this tracker');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/iep-goal-tracker/${selectedTracker.tracker_id}/calendar-event`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `iep-due-${selectedTracker.student_name.replace(/\s+/g, '-')}-${selectedTracker.iep_due_date}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Calendar event downloaded! Import it to your calendar app.');
    } catch (error) {
      console.error('Error downloading calendar event:', error);
      toast.error('Failed to download calendar event');
    }
  };

  // Update IEP due date
  const handleUpdateDueDate = async (newDueDate) => {
    if (!selectedTracker) return;
    
    try {
      await axios.put(
        `${API}/iep-goal-tracker/${selectedTracker.tracker_id}/due-date?iep_due_date=${newDueDate}`,
        {},
        { withCredentials: true }
      );
      toast.success('IEP due date updated!');
      fetchTrackerDetails(selectedTracker.tracker_id);
      fetchData();
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Failed to update IEP due date');
    }
  };

  // Calculate days until IEP due date
  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // PDF Generation for Progress Graph
  const generateProgressPDF = async () => {
    if (!selectedTracker || !selectedTracker.entries?.length) {
      toast.error('No progress data to export');
      return;
    }

    try {
      // Use html2canvas and jsPDF approach via backend or inline
      const chartData = getChartData();
      const baselineType = selectedTracker.baseline_type || 'percentage';
      
      // Create a printable HTML document
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Progress Report - ${selectedTracker.student_name}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            .header { text-align: center; border-bottom: 2px solid #6366F1; padding-bottom: 20px; margin-bottom: 30px; }
            .header h1 { color: #6366F1; margin: 0; font-size: 28px; }
            .header p { color: #666; margin: 5px 0; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .info-box { background: #f8f9fa; padding: 15px; border-radius: 8px; }
            .info-box label { font-size: 12px; color: #888; text-transform: uppercase; }
            .info-box p { margin: 5px 0 0; font-size: 16px; font-weight: 600; color: #333; }
            .goal-text { background: #f0f0ff; padding: 20px; border-radius: 8px; margin-bottom: 30px; border-left: 4px solid #6366F1; }
            .goal-text h3 { margin: 0 0 10px; color: #6366F1; }
            .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
            .stat-box { text-align: center; padding: 15px; background: #f8f9fa; border-radius: 8px; }
            .stat-box .value { font-size: 24px; font-weight: bold; color: #6366F1; }
            .stat-box .label { font-size: 12px; color: #666; }
            .chart-placeholder { background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px; }
            .chart-placeholder h3 { margin: 0 0 15px; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #eee; }
            th { background: #6366F1; color: white; }
            tr:nth-child(even) { background: #f8f9fa; }
            .footer { margin-top: 40px; text-align: center; color: #888; font-size: 12px; border-top: 1px solid #eee; padding-top: 20px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>IEP Goal Progress Report</h1>
            <p>Generated on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <label>Student Name</label>
              <p>${selectedTracker.student_name}</p>
            </div>
            <div class="info-box">
              <label>Goal Domain</label>
              <p>${selectedTracker.goal_domain || 'General'}</p>
            </div>
            <div class="info-box">
              <label>Tracking Method</label>
              <p>${baselineType === 'number' ? 'Number Correct (X/Y)' : 'Percentage Accuracy'}</p>
            </div>
            <div class="info-box">
              <label>Target Date</label>
              <p>${selectedTracker.target_date || 'Not set'}</p>
            </div>
          </div>
          
          <div class="goal-text">
            <h3>${selectedTracker.goal_title}</h3>
            <p>${selectedTracker.goal_text || 'No goal text available'}</p>
          </div>
          
          <div class="stats">
            <div class="stat-box">
              <div class="value">${selectedTracker.stats?.total_entries || 0}</div>
              <div class="label">Total Entries</div>
            </div>
            <div class="stat-box">
              <div class="value">${selectedTracker.stats?.average_score || 0}%</div>
              <div class="label">Average Accuracy</div>
            </div>
            <div class="stat-box">
              <div class="value">${baselineType === 'number' ? selectedTracker.stats?.highest_score : selectedTracker.stats?.highest_score + '%'}</div>
              <div class="label">Highest Score</div>
            </div>
            <div class="stat-box">
              <div class="value">${selectedTracker.baseline_value}${baselineType === 'percentage' ? '%' : '/' + selectedTracker.baseline_total}</div>
              <div class="label">Baseline</div>
            </div>
          </div>
          
          <div class="chart-placeholder">
            <h3>Progress Over Time</h3>
            <svg width="100%" height="220" viewBox="0 0 700 220">
              <!-- Y-axis label -->
              <text x="15" y="110" font-size="11" fill="#374151" transform="rotate(-90, 15, 110)" text-anchor="middle">Percentage of opportunities</text>
              
              <!-- Grid lines (horizontal only, like the example) -->
              ${[0, 25, 50, 75, 100].map((v) => `
                <line x1="50" y1="${180 - (v * 1.6)}" x2="680" y2="${180 - (v * 1.6)}" stroke="#E5E7EB" stroke-width="1"/>
                <text x="45" y="${185 - (v * 1.6)}" text-anchor="end" font-size="10" fill="#374151">${v}</text>
              `).join('')}
              
              <!-- Y-axis -->
              <line x1="50" y1="20" x2="50" y2="180" stroke="#374151" stroke-width="1"/>
              
              <!-- X-axis -->
              <line x1="50" y1="180" x2="680" y2="180" stroke="#374151" stroke-width="1"/>
              
              <!-- X-axis label -->
              <text x="365" y="210" font-size="11" fill="#374151" text-anchor="middle">Date</text>
              
              <!-- Data line (black, straight connections) -->
              <polyline 
                fill="none" 
                stroke="#1F2937" 
                stroke-width="1.5"
                points="${chartData.map((d, i) => `${50 + (i * (630 / Math.max(chartData.length - 1, 1)))},${180 - (d.percentage * 1.6)}`).join(' ')}"
              />
              
              <!-- Data points (black dots) -->
              ${chartData.map((d, i) => `
                <circle cx="${50 + (i * (630 / Math.max(chartData.length - 1, 1)))}" cy="${180 - (d.percentage * 1.6)}" r="4" fill="#1F2937"/>
              `).join('')}
              
              <!-- X-axis date labels -->
              ${chartData.filter((d, i) => i === 0 || i === chartData.length - 1 || (chartData.length > 5 && i % Math.ceil(chartData.length / 5) === 0)).map((d, idx, arr) => {
                const i = chartData.indexOf(d);
                const date = new Date(d.date);
                const label = `${date.getMonth() + 1}-${date.getDate()}-${String(date.getFullYear()).slice(-2)}`;
                return `<text x="${50 + (i * (630 / Math.max(chartData.length - 1, 1)))}" y="195" font-size="9" fill="#374151" text-anchor="middle">${label}</text>`;
              }).join('')}
            </svg>
          </div>
          
          <h3>Progress Entry Log</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Score</th>
                <th>Accuracy</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${selectedTracker.entries.slice().reverse().map(e => `
                <tr>
                  <td>${e.date}</td>
                  <td>${baselineType === 'number' && e.total_possible ? `${e.score}/${e.total_possible}` : `${e.score}%`}</td>
                  <td>${baselineType === 'number' && e.total_possible ? Math.round((e.score / e.total_possible) * 100) : e.score}%</td>
                  <td>${e.notes || '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Generated by SpEdKit • IEP Goal Tracking System</p>
            <p>This report contains confidential student information. Handle in accordance with FERPA guidelines.</p>
          </div>
        </body>
        </html>
      `;
      
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Auto-trigger print dialog after a short delay
      setTimeout(() => {
        printWindow.print();
      }, 500);
      
      toast.success('Progress report ready for printing/saving as PDF');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate report');
    }
  };

  // Prepare chart data
  const getChartData = () => {
    if (!selectedTracker?.entries?.length) return [];
    
    const baselineType = selectedTracker.baseline_type || 'percentage';
    
    return selectedTracker.entries.map(entry => {
      let percentage;
      if (baselineType === 'number' && entry.total_possible) {
        percentage = Math.round((entry.score / entry.total_possible) * 100);
      } else {
        percentage = entry.score;
      }
      
      return {
        date: entry.date,
        score: entry.score,
        total_possible: entry.total_possible,
        percentage,
        notes: entry.notes
      };
    });
  };

  const getScoreColor = (score, totalPossible = null, baselineType = 'percentage') => {
    let percentage;
    if (baselineType === 'number' && totalPossible) {
      percentage = (score / totalPossible) * 100;
    } else {
      percentage = score;
    }
    
    if (percentage >= 80) return '#5D8D78';
    if (percentage >= 60) return '#22C55E';
    if (percentage >= 40) return '#EAB308';
    if (percentage >= 20) return '#F97316';
    return '#EF4444';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-primary" />;
    if (trend === 'declining') return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
    return <span className="text-text-muted text-xs">--</span>;
  };

  // Format score for display based on baseline type
  const formatScore = (entry, baselineType) => {
    if (baselineType === 'number' && entry.total_possible) {
      return `${entry.score}/${entry.total_possible}`;
    }
    return `${entry.score}%`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="iep-goal-tracker-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/iep-goals')}
              variant="ghost"
              className="text-text-secondary hover:text-primary"
            >
              <Target className="w-4 h-4 mr-2" />
              IEP Goals
            </Button>
            <Button
              onClick={() => navigate('/progress')}
              variant="ghost"
              className="text-text-secondary hover:text-primary"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Skill Progress
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-text-primary mb-1">
              <Target className="w-8 h-8 inline mr-2 text-[#6366F1]" />
              IEP Goal Progress Tracker
            </h1>
            <p className="font-body text-text-secondary">
              Track progress on your saved IEP goals for each student
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setBulkGoalFilter('');
                setSelectedTrackersForBulk([]);
                setBulkEntry({ date: new Date().toISOString().split('T')[0], score: 0, total_possible: 10, notes: '', data_point: '' });
                setShowBulkEntryDialog(true);
              }}
              variant="outline"
              className="rounded-full border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1]/10"
              data-testid="bulk-entry-btn"
              disabled={trackers.length === 0}
            >
              <ListChecks className="w-4 h-4 mr-2" />
              Bulk Entry
            </Button>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-full"
              data-testid="create-tracker-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Tracker
            </Button>
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Trackers List */}
          <div className="lg:col-span-1">
            <h2 className="font-heading font-semibold text-lg text-text-primary mb-4">Active Trackers</h2>
            
            {loading ? (
              <div className="bg-white rounded-2xl p-8 shadow-card text-center">
                <div className="w-8 h-8 border-4 border-[#6366F1] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : trackers.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 shadow-card text-center">
                <Target className="w-12 h-12 text-[#6366F1]/30 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">No trackers yet</h3>
                <p className="font-body text-text-secondary text-sm mb-4">
                  Create a tracker to monitor progress on your saved IEP goals
                </p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-full"
                >
                  Create First Tracker
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {trackers.map((tracker) => (
                  <div
                    key={tracker.tracker_id}
                    onClick={() => {
                      setSelectedTracker(tracker);
                      fetchTrackerDetails(tracker.tracker_id);
                    }}
                    className={`bg-white rounded-xl p-4 shadow-card cursor-pointer transition-all hover:shadow-float ${
                      selectedTracker?.tracker_id === tracker.tracker_id ? 'ring-2 ring-[#6366F1]' : ''
                    }`}
                    data-testid={`tracker-card-${tracker.tracker_id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-text-primary text-sm truncate">
                          {tracker.goal_title}
                        </h3>
                        <p className="text-xs text-text-muted truncate">{tracker.student_name}</p>
                      </div>
                      <span
                        className="text-xs px-2 py-1 rounded-full"
                        style={{ 
                          backgroundColor: STATUS_OPTIONS.find(s => s.value === tracker.status)?.color + '20',
                          color: STATUS_OPTIONS.find(s => s.value === tracker.status)?.color
                        }}
                      >
                        {STATUS_OPTIONS.find(s => s.value === tracker.status)?.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-text-muted">
                      <span>{tracker.entries_count || 0} entries</span>
                      <div className="flex items-center gap-2">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          tracker.baseline_type === 'number' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {tracker.baseline_type === 'number' ? '#' : '%'}
                        </span>
                        {getTrendIcon(tracker.trend)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tracker Details */}
          <div className="lg:col-span-2">
            {selectedTracker ? (
              <div className="bg-white rounded-2xl shadow-card overflow-hidden">
                {/* Tracker Header */}
                <div className="bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] p-6 text-white">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h2 className="font-heading font-bold text-xl mb-1">{selectedTracker.goal_title}</h2>
                      <p className="text-white/80 text-sm mb-2">{selectedTracker.student_name}</p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
                        <span>{selectedTracker.goal_domain}</span>
                        {selectedTracker.target_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Target: {selectedTracker.target_date}
                          </span>
                        )}
                        {selectedTracker.iep_due_date && (
                          <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${
                            getDaysUntilDue(selectedTracker.iep_due_date) <= 0 
                              ? 'bg-red-500/30 text-white' 
                              : getDaysUntilDue(selectedTracker.iep_due_date) <= 30 
                                ? 'bg-yellow-500/30 text-white' 
                                : 'bg-white/20'
                          }`}>
                            <Bell className="w-3 h-3" />
                            IEP Due: {selectedTracker.iep_due_date}
                            {getDaysUntilDue(selectedTracker.iep_due_date) <= 0 
                              ? ' (Overdue!)' 
                              : ` (${getDaysUntilDue(selectedTracker.iep_due_date)} days)`}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Select value={selectedTracker.status} onValueChange={(v) => handleStatusChange(selectedTracker.tracker_id, v)}>
                        <SelectTrigger className="w-36 bg-white/20 border-white/30 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setTrackerToCopy(selectedTracker);
                          setSelectedStudentsForCopy([]);
                          setShowCopyDialog(true);
                        }}
                        className="text-white/70 hover:text-white hover:bg-white/20"
                        title="Copy to other students"
                        data-testid="copy-tracker-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setTrackerToDelete(selectedTracker);
                          setShowDeleteDialog(true);
                        }}
                        className="text-white/80 hover:text-white hover:bg-white/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* IEP Due Date Section */}
                {selectedTracker.iep_due_date ? (
                  <div className="px-6 py-3 bg-amber-50 border-b flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">
                        IEP Annual Review Due: {selectedTracker.iep_due_date}
                        {getDaysUntilDue(selectedTracker.iep_due_date) <= 0 
                          ? <span className="text-red-600 ml-2">(Overdue!)</span> 
                          : <span className="text-amber-600 ml-2">({getDaysUntilDue(selectedTracker.iep_due_date)} days remaining)</span>}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowReminderDialog(true)}
                        variant="outline"
                        size="sm"
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                        data-testid="send-reminder-btn"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Send Reminder
                      </Button>
                      <Button
                        onClick={handleDownloadCalendarEvent}
                        variant="outline"
                        size="sm"
                        className="text-xs border-amber-300 text-amber-700 hover:bg-amber-100"
                        data-testid="add-to-calendar-btn"
                      >
                        <CalendarPlus className="w-3 h-3 mr-1" />
                        Add to Calendar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="px-6 py-3 bg-gray-50 border-b">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">No IEP due date set</span>
                      <Input
                        type="date"
                        className="w-40 h-8 text-sm ml-2"
                        onChange={(e) => handleUpdateDueDate(e.target.value)}
                        data-testid="set-iep-due-date"
                      />
                    </div>
                  </div>
                )}

                {/* Goal Text */}
                <div className="p-6 border-b">
                  <h3 className="font-heading font-semibold text-text-primary mb-2">Annual Goal</h3>
                  <p className="font-body text-text-secondary text-sm leading-relaxed">
                    {selectedTracker.goal_text || 'No goal text available'}
                  </p>
                </div>

                {/* Progress Chart */}
                <div className="p-6 border-b">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-heading font-semibold text-text-primary">Progress Data</h3>
                    <div className="flex gap-2">
                      {selectedTracker.entries?.length > 0 && (
                        <Button
                          onClick={generateProgressPDF}
                          variant="outline"
                          className="rounded-full border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1]/10"
                          data-testid="export-pdf-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export PDF
                        </Button>
                      )}
                      <Button
                        onClick={() => setShowAddEntryDialog(true)}
                        className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-full"
                        data-testid="add-entry-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Record Progress
                      </Button>
                    </div>
                  </div>

                  {/* Stats Summary */}
                  {selectedTracker.stats?.total_entries > 0 && (
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="bg-secondary/30 rounded-xl p-3 text-center">
                        <p className="text-2xl font-heading font-bold text-[#6366F1]">{selectedTracker.stats.total_entries}</p>
                        <p className="text-xs text-text-muted">Entries</p>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-3 text-center">
                        <p className="text-2xl font-heading font-bold text-[#6366F1]">
                          {selectedTracker.stats.average_score}%
                        </p>
                        <p className="text-xs text-text-muted">Avg Accuracy</p>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-3 text-center">
                        <p className="text-2xl font-heading font-bold text-green-600">
                          {selectedTracker.baseline_type === 'number' 
                            ? selectedTracker.stats.highest_score 
                            : `${selectedTracker.stats.highest_score}%`}
                        </p>
                        <p className="text-xs text-text-muted">Highest</p>
                      </div>
                      <div className="bg-secondary/30 rounded-xl p-3 text-center">
                        <p className="text-2xl font-heading font-bold text-orange-500">
                          {selectedTracker.baseline_type === 'number' 
                            ? selectedTracker.stats.lowest_score 
                            : `${selectedTracker.stats.lowest_score}%`}
                        </p>
                        <p className="text-xs text-text-muted">Lowest</p>
                      </div>
                    </div>
                  )}

                  {/* Baseline Info */}
                  <div className="mb-4 p-3 bg-[#6366F1]/5 rounded-lg border border-[#6366F1]/20">
                    <p className="text-sm text-text-secondary">
                      <span className="font-semibold text-[#6366F1]">Tracking Method: </span>
                      {selectedTracker.baseline_type === 'number' 
                        ? 'Number Correct (X out of Y)'
                        : 'Percentage Accuracy (0-100%)'}
                      {selectedTracker.baseline_value !== undefined && selectedTracker.baseline_value !== null && (
                        <span className="ml-2">
                          | <span className="font-semibold">Baseline: </span>
                          {selectedTracker.baseline_type === 'number' 
                            ? `${selectedTracker.baseline_value}/${selectedTracker.baseline_total || '?'}`
                            : `${selectedTracker.baseline_value}%`}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Line Chart */}
                  {selectedTracker.entries?.length > 0 ? (
                    <div className="bg-white rounded-xl p-4 border border-gray-100" ref={chartRef}>
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={getChartData()} margin={{ top: 20, right: 20, left: 10, bottom: 30 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 11, fill: '#374151' }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getMonth() + 1}-${date.getDate()}-${String(date.getFullYear()).slice(-2)}`;
                            }}
                            label={{ value: 'Date', position: 'bottom', offset: 15, fontSize: 12, fill: '#374151' }}
                            axisLine={{ stroke: '#374151' }}
                            tickLine={{ stroke: '#374151' }}
                          />
                          <YAxis 
                            domain={[0, 100]} 
                            ticks={[0, 25, 50, 75, 100]}
                            tick={{ fontSize: 11, fill: '#374151' }}
                            label={{ value: 'Percentage of opportunities', angle: -90, position: 'insideLeft', offset: 10, fontSize: 12, fill: '#374151', style: { textAnchor: 'middle' } }}
                            axisLine={{ stroke: '#374151' }}
                            tickLine={{ stroke: '#374151' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              fontSize: '12px'
                            }}
                            formatter={(value, name) => [`${value}%`, 'Accuracy']}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Line 
                            type="linear" 
                            dataKey="percentage" 
                            stroke="#1F2937" 
                            strokeWidth={1.5}
                            dot={{ fill: '#1F2937', stroke: '#1F2937', strokeWidth: 1, r: 4 }}
                            activeDot={{ r: 6, fill: '#1F2937' }}
                            connectNulls={true}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="bg-secondary/20 rounded-xl p-8 text-center">
                      <LineChartIcon className="w-10 h-10 text-text-muted/30 mx-auto mb-2" />
                      <p className="text-text-muted text-sm">No progress data yet. Record your first entry!</p>
                    </div>
                  )}
                </div>

                {/* Entries List */}
                {selectedTracker.entries?.length > 0 && (
                  <div className="p-6">
                    <h3 className="font-heading font-semibold text-text-primary mb-4">Recent Entries</h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {selectedTracker.entries.slice().reverse().slice(0, 10).map((entry) => {
                        const baselineType = selectedTracker.baseline_type || 'percentage';
                        return (
                          <div key={entry.entry_id} className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg group">
                            <div
                              className="w-12 h-10 rounded-lg flex items-center justify-center text-white font-heading font-bold text-sm"
                              style={{ backgroundColor: getScoreColor(entry.score, entry.total_possible, baselineType) }}
                            >
                              {formatScore(entry, baselineType)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-text-primary">{entry.date}</span>
                                {entry.data_point && (
                                  <span className="text-xs bg-[#6366F1]/10 text-[#6366F1] px-2 py-0.5 rounded-full">
                                    {entry.data_point}
                                  </span>
                                )}
                              </div>
                              {entry.notes && (
                                <p className="text-sm text-text-secondary">{entry.notes}</p>
                              )}
                            </div>
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-text-muted hover:text-[#6366F1] hover:bg-[#6366F1]/10"
                                onClick={() => openEditEntryDialog(entry)}
                                data-testid={`edit-entry-${entry.entry_id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-text-muted hover:text-red-500 hover:bg-red-50"
                                onClick={() => {
                                  setEntryToDelete(entry);
                                  setShowDeleteEntryDialog(true);
                                }}
                                data-testid={`delete-entry-${entry.entry_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-12 shadow-card text-center">
                <Target className="w-16 h-16 text-[#6366F1]/20 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  Select a Tracker
                </h3>
                <p className="font-body text-text-secondary mb-4">
                  Choose a tracker from the list to view and record progress
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create Tracker Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create IEP Goal Tracker</DialogTitle>
            <DialogDescription className="font-body">
              Link a saved IEP goal to a student to track their progress
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Select Goal from Goal Bank</Label>
              <Select 
                value={newTracker.goal_bank_id} 
                onValueChange={(v) => setNewTracker({ ...newTracker, goal_bank_id: v })}
              >
                <SelectTrigger className="mt-2" data-testid="goal-select">
                  <SelectValue placeholder="Choose a saved goal" />
                </SelectTrigger>
                <SelectContent>
                  {savedGoals.map((goal) => (
                    <SelectItem key={goal.goal_id} value={goal.goal_id}>
                      {goal.title} ({goal.goal_domain})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savedGoals.length === 0 && (
                <p className="text-xs text-text-muted mt-1">
                  No saved goals. <button onClick={() => navigate('/iep-goals')} className="text-[#6366F1] underline">Create and save goals first</button>
                </p>
              )}
            </div>
            <div>
              <Label className="font-body font-semibold">Select Student</Label>
              <Select 
                value={newTracker.student_id} 
                onValueChange={(v) => setNewTracker({ ...newTracker, student_id: v })}
              >
                <SelectTrigger className="mt-2" data-testid="student-select">
                  <SelectValue placeholder="Choose a student" />
                </SelectTrigger>
                <SelectContent>
                  {folders.map((folder) => (
                    <SelectItem key={folder.folder_id} value={folder.folder_id}>
                      {folder.student_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {folders.length === 0 && (
                <p className="text-xs text-text-muted mt-1">
                  No students. <button onClick={() => navigate('/students')} className="text-[#6366F1] underline">Create student folders first</button>
                </p>
              )}
            </div>
            <div>
              <Label className="font-body font-semibold">Target Date (Optional)</Label>
              <Input
                type="date"
                value={newTracker.target_date}
                onChange={(e) => setNewTracker({ ...newTracker, target_date: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label className="font-body font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#6366F1]" />
                IEP Annual Review Due Date (Optional)
              </Label>
              <Input
                type="date"
                value={newTracker.iep_due_date}
                onChange={(e) => setNewTracker({ ...newTracker, iep_due_date: e.target.value })}
                className="mt-2"
                data-testid="iep-due-date-input"
              />
              <p className="text-xs text-text-muted mt-1">
                Set the due date to receive email reminders and add to your calendar
              </p>
            </div>
            
            {/* Baseline Type Selection */}
            <div>
              <Label className="font-body font-semibold">How will you track progress?</Label>
              <div className="mt-2 space-y-2">
                {BASELINE_TYPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      newTracker.baseline_type === opt.value
                        ? 'border-[#6366F1] bg-[#6366F1]/5'
                        : 'border-gray-200 hover:border-[#6366F1]/30'
                    }`}
                  >
                    <input
                      type="radio"
                      name="baseline_type"
                      value={opt.value}
                      checked={newTracker.baseline_type === opt.value}
                      onChange={(e) => setNewTracker({ ...newTracker, baseline_type: e.target.value })}
                      className="mt-1"
                      data-testid={`baseline-type-${opt.value}`}
                    />
                    <div>
                      <p className="font-semibold text-text-primary">{opt.label}</p>
                      <p className="text-sm text-text-muted">{opt.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            
            {/* Baseline Value Input */}
            <div>
              <Label className="font-body font-semibold">
                Baseline {newTracker.baseline_type === 'percentage' ? 'Percentage' : 'Score'} (Starting Point)
              </Label>
              {newTracker.baseline_type === 'percentage' ? (
                <div className="mt-2">
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newTracker.baseline_value}
                      onChange={(e) => setNewTracker({ ...newTracker, baseline_value: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                      className="w-24 text-center text-lg font-bold"
                      data-testid="baseline-percentage-input"
                    />
                    <span className="text-xl font-bold text-[#6366F1]">%</span>
                    <div className="flex-1">
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={newTracker.baseline_value}
                        onChange={(e) => setNewTracker({ ...newTracker, baseline_value: parseInt(e.target.value) })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #6366F1 0%, #6366F1 ${newTracker.baseline_value}%, #E5E7EB ${newTracker.baseline_value}%, #E5E7EB 100%)`
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs text-text-muted">Correct</Label>
                    <Input
                      type="number"
                      min="0"
                      max={newTracker.baseline_total}
                      value={newTracker.baseline_value}
                      onChange={(e) => setNewTracker({ ...newTracker, baseline_value: Math.min(newTracker.baseline_total, Math.max(0, parseInt(e.target.value) || 0)) })}
                      className="text-center text-lg font-bold"
                      data-testid="baseline-number-input"
                    />
                  </div>
                  <span className="text-xl font-bold text-text-muted mt-5">out of</span>
                  <div className="flex-1">
                    <Label className="text-xs text-text-muted">Total</Label>
                    <Input
                      type="number"
                      min="1"
                      value={newTracker.baseline_total}
                      onChange={(e) => setNewTracker({ ...newTracker, baseline_total: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="text-center text-lg font-bold"
                      data-testid="baseline-total-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTracker} 
              disabled={saving || !newTracker.goal_bank_id || !newTracker.student_id}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="create-tracker-submit"
            >
              {saving ? 'Creating...' : 'Create Tracker'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Entry Dialog */}
      <Dialog open={showAddEntryDialog} onOpenChange={setShowAddEntryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Record Progress</DialogTitle>
            <DialogDescription className="font-body">
              Log progress for: {selectedTracker?.goal_title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Date</Label>
              <Input
                type="date"
                value={newEntry.date}
                onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                className="mt-2"
                data-testid="entry-date"
              />
            </div>
            
            {/* Dynamic Score Input based on baseline_type */}
            {(() => {
              const baselineType = selectedTracker?.baseline_type || 'percentage';
              
              if (baselineType === 'percentage') {
                return (
                  <div>
                    <Label className="font-body font-semibold">Percentage Accuracy (0-100%)</Label>
                    <div className="mt-2">
                      <div className="flex items-center gap-4">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={newEntry.score}
                          onChange={(e) => setNewEntry({ ...newEntry, score: Math.min(100, Math.max(0, parseInt(e.target.value) || 0)) })}
                          className="w-24 text-center text-lg font-bold"
                          data-testid="entry-percentage-input"
                        />
                        <span className="text-2xl font-bold text-[#6366F1]">%</span>
                        <div className="flex-1">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={newEntry.score}
                            onChange={(e) => setNewEntry({ ...newEntry, score: parseInt(e.target.value) })}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #6366F1 0%, #6366F1 ${newEntry.score}%, #E5E7EB ${newEntry.score}%, #E5E7EB 100%)`
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-text-muted mt-2">
                        <span>0%</span>
                        <span className={`font-medium ${newEntry.score >= 80 ? 'text-primary' : newEntry.score >= 50 ? 'text-accent' : 'text-destructive'}`}>
                          {newEntry.score >= 80 ? 'Proficient' : newEntry.score >= 50 ? 'Developing' : 'Needs Support'}
                        </span>
                        <span>100%</span>
                      </div>
                    </div>
                  </div>
                );
              } else {
                // number tracking
                return (
                  <div>
                    <Label className="font-body font-semibold">Number Correct</Label>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-text-muted">Correct</Label>
                        <Input
                          type="number"
                          min="0"
                          max={newEntry.total_possible}
                          value={newEntry.score}
                          onChange={(e) => setNewEntry({ ...newEntry, score: Math.min(newEntry.total_possible, Math.max(0, parseInt(e.target.value) || 0)) })}
                          className="text-center text-lg font-bold"
                          data-testid="entry-number-input"
                        />
                      </div>
                      <span className="text-2xl font-bold text-text-muted mt-5">out of</span>
                      <div className="flex-1">
                        <Label className="text-xs text-text-muted">Total</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newEntry.total_possible}
                          onChange={(e) => setNewEntry({ ...newEntry, total_possible: Math.max(1, parseInt(e.target.value) || 1) })}
                          className="text-center text-lg font-bold"
                          data-testid="entry-total-input"
                        />
                      </div>
                    </div>
                    <div className="mt-3 bg-[#6366F1]/5 rounded-lg p-3 text-center">
                      <span className="text-sm text-text-muted">Accuracy: </span>
                      <span className={`text-lg font-bold ${
                        (newEntry.score / newEntry.total_possible) >= 0.8 ? 'text-primary' : 
                        (newEntry.score / newEntry.total_possible) >= 0.5 ? 'text-accent' : 'text-destructive'
                      }`}>
                        {newEntry.total_possible > 0 ? Math.round((newEntry.score / newEntry.total_possible) * 100) : 0}%
                      </span>
                    </div>
                  </div>
                );
              }
            })()}
            
            <div>
              <Label className="font-body font-semibold">Data Point (Optional)</Label>
              <Input
                value={newEntry.data_point}
                onChange={(e) => setNewEntry({ ...newEntry, data_point: e.target.value })}
                placeholder="e.g., additional context or trial info"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Notes (Optional)</Label>
              <Textarea
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                placeholder="Observations, context, or next steps..."
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEntryDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleAddEntry} 
              disabled={saving}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="add-entry-submit"
            >
              {saving ? 'Saving...' : 'Save Progress'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Entry Dialog */}
      <Dialog open={showBulkEntryDialog} onOpenChange={setShowBulkEntryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-[#6366F1]" />
              Bulk Progress Entry
            </DialogTitle>
            <DialogDescription className="font-body">
              Record the same progress for multiple students at once - great for group activities.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Filter by Goal */}
            <div>
              <Label className="font-body font-semibold">Filter by Goal (Optional)</Label>
              <Select value={bulkGoalFilter || "__all__"} onValueChange={(v) => {
                setBulkGoalFilter(v === "__all__" ? "" : v);
                setSelectedTrackersForBulk([]);
              }}>
                <SelectTrigger className="mt-2" data-testid="bulk-goal-filter">
                  <SelectValue placeholder="All goals" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All goals</SelectItem>
                  {getUniqueGoalsFromTrackers().map((goal) => (
                    <SelectItem key={goal.id} value={goal.id}>{goal.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Select Trackers */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="font-body font-semibold">Select Trackers</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const available = getTrackersForBulkEntry();
                    if (selectedTrackersForBulk.length === available.length) {
                      setSelectedTrackersForBulk([]);
                    } else {
                      setSelectedTrackersForBulk(available.map(t => t.tracker_id));
                    }
                  }}
                  className="text-xs text-[#6366F1]"
                >
                  {selectedTrackersForBulk.length === getTrackersForBulkEntry().length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                {getTrackersForBulkEntry().length === 0 ? (
                  <p className="text-sm text-text-muted text-center py-4">No trackers available</p>
                ) : (
                  getTrackersForBulkEntry().map((tracker) => (
                    <label
                      key={tracker.tracker_id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                        selectedTrackersForBulk.includes(tracker.tracker_id)
                          ? 'bg-[#6366F1]/10'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedTrackersForBulk.includes(tracker.tracker_id)}
                        onCheckedChange={() => toggleTrackerForBulk(tracker.tracker_id)}
                        data-testid={`bulk-tracker-${tracker.tracker_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-text-primary truncate">{tracker.student_name}</p>
                        <p className="text-xs text-text-muted truncate">{tracker.goal_title}</p>
                      </div>
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        tracker.baseline_type === 'number' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {tracker.baseline_type === 'number' ? '#' : '%'}
                      </span>
                    </label>
                  ))
                )}
              </div>
              {selectedTrackersForBulk.length > 0 && (
                <p className="text-sm text-[#6366F1] mt-2">
                  {selectedTrackersForBulk.length} tracker{selectedTrackersForBulk.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            
            {/* Date */}
            <div>
              <Label className="font-body font-semibold">Date</Label>
              <Input
                type="date"
                value={bulkEntry.date}
                onChange={(e) => setBulkEntry({ ...bulkEntry, date: e.target.value })}
                className="mt-2"
                data-testid="bulk-entry-date"
              />
            </div>
            
            {/* Score - Show percentage input (works for both types) */}
            <div>
              <Label className="font-body font-semibold">Score</Label>
              <p className="text-xs text-text-muted mb-2">
                For percentage trackers: enter % accuracy. For number trackers: enter correct count.
              </p>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={bulkEntry.score}
                  onChange={(e) => setBulkEntry({ ...bulkEntry, score: Math.max(0, parseInt(e.target.value) || 0) })}
                  className="w-24 text-center text-lg font-bold"
                  data-testid="bulk-entry-score"
                />
                <div className="flex-1">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={Math.min(bulkEntry.score, 100)}
                    onChange={(e) => setBulkEntry({ ...bulkEntry, score: parseInt(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #6366F1 0%, #6366F1 ${Math.min(bulkEntry.score, 100)}%, #E5E7EB ${Math.min(bulkEntry.score, 100)}%, #E5E7EB 100%)`
                    }}
                  />
                </div>
              </div>
            </div>
            
            {/* Total Possible (for number trackers) */}
            <div>
              <Label className="font-body font-semibold">Total Possible (for number trackers only)</Label>
              <Input
                type="number"
                min="1"
                value={bulkEntry.total_possible}
                onChange={(e) => setBulkEntry({ ...bulkEntry, total_possible: Math.max(1, parseInt(e.target.value) || 1) })}
                className="mt-2 w-24"
                data-testid="bulk-entry-total"
              />
            </div>
            
            {/* Notes */}
            <div>
              <Label className="font-body font-semibold">Notes (Optional)</Label>
              <Textarea
                value={bulkEntry.notes}
                onChange={(e) => setBulkEntry({ ...bulkEntry, notes: e.target.value })}
                placeholder="Notes for all selected trackers..."
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkEntryDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleBulkEntry} 
              disabled={saving || selectedTrackersForBulk.length === 0}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="bulk-entry-submit"
            >
              {saving ? 'Saving...' : `Record for ${selectedTrackersForBulk.length} Tracker${selectedTrackersForBulk.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete tracker?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will delete the tracker for "{trackerToDelete?.goal_title}" and all its progress entries. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTracker}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy to Other Students Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#6366F1]" />
              Copy Tracker to Other Students
            </DialogTitle>
            <DialogDescription className="font-body">
              Create the same tracker for multiple students at once. Select which students should have this goal tracker.
            </DialogDescription>
          </DialogHeader>
          
          {trackerToCopy && (
            <div className="py-4">
              {/* Tracker Info */}
              <div className="bg-[#6366F1]/5 rounded-xl p-4 mb-4">
                <p className="font-semibold text-text-primary">{trackerToCopy.goal_title}</p>
                <div className="flex items-center gap-3 mt-2 text-sm text-text-muted">
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    trackerToCopy.baseline_type === 'number' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {trackerToCopy.baseline_type === 'number' ? 'Number Correct' : 'Percentage'}
                  </span>
                  <span>
                    Baseline: {trackerToCopy.baseline_type === 'number' 
                      ? `${trackerToCopy.baseline_value}/${trackerToCopy.baseline_total}`
                      : `${trackerToCopy.baseline_value}%`}
                  </span>
                </div>
              </div>
              
              {/* Student Selection */}
              <Label className="font-body font-semibold mb-3 block">Select Students</Label>
              {(() => {
                const availableStudents = getAvailableStudentsForCopy();
                
                if (availableStudents.length === 0) {
                  return (
                    <div className="text-center py-6 bg-gray-50 rounded-xl">
                      <Users className="w-10 h-10 text-text-muted/30 mx-auto mb-2" />
                      <p className="text-text-muted text-sm">
                        All students already have this tracker, or no other students exist.
                      </p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {availableStudents.map((folder) => (
                      <label
                        key={folder.folder_id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedStudentsForCopy.includes(folder.folder_id)
                            ? 'border-[#6366F1] bg-[#6366F1]/5'
                            : 'border-gray-200 hover:border-[#6366F1]/30'
                        }`}
                      >
                        <Checkbox
                          checked={selectedStudentsForCopy.includes(folder.folder_id)}
                          onCheckedChange={() => toggleStudentForCopy(folder.folder_id)}
                          data-testid={`copy-student-${folder.folder_id}`}
                        />
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-[#6366F1]" />
                          <span className="font-medium text-text-primary">{folder.student_name}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                );
              })()}
              
              {selectedStudentsForCopy.length > 0 && (
                <p className="text-sm text-[#6366F1] mt-3">
                  {selectedStudentsForCopy.length} student{selectedStudentsForCopy.length > 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCopyTracker} 
              disabled={saving || selectedStudentsForCopy.length === 0}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="copy-tracker-submit"
            >
              {saving ? 'Copying...' : `Copy to ${selectedStudentsForCopy.length || 0} Student${selectedStudentsForCopy.length !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Reminder Email Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#6366F1]" />
              Send IEP Reminder Email
            </DialogTitle>
            <DialogDescription className="font-body">
              Send an email reminder about the upcoming IEP due date for {selectedTracker?.student_name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {selectedTracker?.iep_due_date && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>IEP Due Date:</strong> {selectedTracker.iep_due_date}
                  <span className="ml-2">
                    ({getDaysUntilDue(selectedTracker.iep_due_date)} days remaining)
                  </span>
                </p>
              </div>
            )}
            
            <div>
              <Label className="font-body font-semibold">Recipient Email *</Label>
              <Input
                type="email"
                value={reminderEmail}
                onChange={(e) => setReminderEmail(e.target.value)}
                placeholder="teacher@school.edu"
                className="mt-2"
                data-testid="reminder-email-input"
              />
            </div>
            
            <div>
              <Label className="font-body font-semibold">Custom Message (Optional)</Label>
              <Textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                placeholder="Add a personal note to the reminder email..."
                className="mt-2"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowReminderDialog(false);
                setReminderEmail('');
                setReminderMessage('');
              }} 
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSendReminder} 
              disabled={saving || !reminderEmail}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="send-reminder-submit"
            >
              {saving ? 'Sending...' : 'Send Reminder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={showEditEntryDialog} onOpenChange={setShowEditEntryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#6366F1]" />
              Edit Progress Entry
            </DialogTitle>
            <DialogDescription className="font-body">
              Update this progress entry for {selectedTracker?.student_name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="font-body font-semibold">Date *</Label>
              <Input
                type="date"
                value={editEntry.date}
                onChange={(e) => setEditEntry({ ...editEntry, date: e.target.value })}
                className="mt-2"
                data-testid="edit-entry-date"
              />
            </div>
            
            {selectedTracker?.baseline_type === 'percentage' ? (
              <div>
                <Label className="font-body font-semibold">Percentage Accuracy *</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editEntry.score}
                    onChange={(e) => setEditEntry({ ...editEntry, score: parseInt(e.target.value) || 0 })}
                    className="w-24"
                    data-testid="edit-entry-score"
                  />
                  <span className="text-text-secondary">%</span>
                </div>
              </div>
            ) : (
              <div>
                <Label className="font-body font-semibold">Score *</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="number"
                    min="0"
                    value={editEntry.score}
                    onChange={(e) => setEditEntry({ ...editEntry, score: parseInt(e.target.value) || 0 })}
                    className="w-20"
                    data-testid="edit-entry-score"
                  />
                  <span className="text-text-secondary">out of</span>
                  <Input
                    type="number"
                    min="1"
                    value={editEntry.total_possible}
                    onChange={(e) => setEditEntry({ ...editEntry, total_possible: parseInt(e.target.value) || 1 })}
                    className="w-20"
                    data-testid="edit-entry-total"
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label className="font-body font-semibold">Data Point (Optional)</Label>
              <Input
                value={editEntry.data_point}
                onChange={(e) => setEditEntry({ ...editEntry, data_point: e.target.value })}
                placeholder="e.g., 3 out of 5 trials"
                className="mt-2"
              />
            </div>
            
            <div>
              <Label className="font-body font-semibold">Notes (Optional)</Label>
              <Textarea
                value={editEntry.notes}
                onChange={(e) => setEditEntry({ ...editEntry, notes: e.target.value })}
                placeholder="Add observation notes..."
                className="mt-2"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditEntryDialog(false);
                setEntryToEdit(null);
              }} 
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditEntry} 
              disabled={saving || !editEntry.date}
              className="bg-[#6366F1] text-white rounded-full"
              data-testid="save-edit-entry"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Entry Confirmation Dialog */}
      <AlertDialog open={showDeleteEntryDialog} onOpenChange={setShowDeleteEntryDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Progress Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this progress entry from {entryToDelete?.date}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => {
                setShowDeleteEntryDialog(false);
                setEntryToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEntry}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-entry"
            >
              Delete Entry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IEPGoalTracker;
